import { isNil } from 'lodash';
import { MethodologyPoints } from '../constants';
import { calculateScore } from './shared';
import type { CalculatePoints, Question } from './types';
import { CalculateMethodology } from '../utils';
import { InputsReducer } from 'src/redux/reducers/inputs';
import { IntroAPROrRewards } from 'src/types/inputs';
import {
  CardsMarketplaceOffer,
  CardsMarketplaceOfferPromos,
  CardsMarketplaceOfferRewardsCategory,
} from 'src/types/marketplace';

const {
  THIRTY_SEVEN_POINTS,
  TWENTY_ONE_POINTS,
  THIRTEEN_POINTS,
  EIGHT_POINTS,
  FIVE_POINTS,
  THREE_POINTS,
  ZERO_POINTS,
} = MethodologyPoints;

export const calculateDebtRewards: CalculatePoints = (
  { introAPROrRewards },
  offer: CardsMarketplaceOffer
) => {
  const offerIntroTime = offer?.details?.balance_transfer_intro_time;
  if (isNil(offerIntroTime)) {
    return ZERO_POINTS;
  }

  if (introAPROrRewards === IntroAPROrRewards.IntroAPR) {
    if (offerIntroTime >= 21) {
      return TWENTY_ONE_POINTS;
    }
    if (offerIntroTime >= 18 && offerIntroTime < 21) {
      return THIRTEEN_POINTS;
    }
    if (offerIntroTime >= 15 && offerIntroTime < 18) {
      return EIGHT_POINTS;
    }
    if (offerIntroTime >= 12 && offerIntroTime < 15) {
      return FIVE_POINTS;
    }
    if (offerIntroTime >= 1 && offerIntroTime < 12) {
      return THREE_POINTS;
    }

    return ZERO_POINTS;
  }

  const rewards = offer?.details?.rewards;
  let allRewardsRate = false;
  const bonusCategories = rewards?.filter((reward) => {
    if (
      reward?.category &&
      reward?.category === CardsMarketplaceOfferRewardsCategory.All
    ) {
      if (reward?.rate && reward.rate > 1) {
        allRewardsRate = true;
      }
    }
    return reward?.rate && reward.rate >= 1;
  });

  const hasBonusCategory = bonusCategories && bonusCategories.length > 0;
  let rewardsPoints = hasBonusCategory ? EIGHT_POINTS : ZERO_POINTS;
  rewardsPoints += allRewardsRate ? EIGHT_POINTS : ZERO_POINTS;

  if (offerIntroTime >= 21) {
    rewardsPoints += TWENTY_ONE_POINTS;
  }
  if (offerIntroTime >= 18 && offerIntroTime < 21) {
    rewardsPoints += THIRTEEN_POINTS;
  }
  if (offerIntroTime >= 15 && offerIntroTime < 18) {
    rewardsPoints += EIGHT_POINTS;
  }
  if (offerIntroTime >= 12 && offerIntroTime < 15) {
    rewardsPoints += FIVE_POINTS;
  }
  if (offerIntroTime >= 1 && offerIntroTime < 12) {
    rewardsPoints += THREE_POINTS;
  }
  if (offerIntroTime === 0) {
    rewardsPoints += ZERO_POINTS;
  }
  return rewardsPoints;
};

export const calculateMonthlySpend: CalculatePoints = (
  { monthlySpend },
  { details: { promos } }: CardsMarketplaceOffer
) =>
  promos?.some(
    (promo: CardsMarketplaceOfferPromos) =>
      monthlySpend &&
      promo?.spend_months &&
      monthlySpend * promo?.spend_months >= (promo?.spend_amount ?? 0)
  )
    ? EIGHT_POINTS
    : ZERO_POINTS;

const getQuestions = ({ introAPROrRewards }: InputsReducer): Question[] => [
  {
    availablePoints:
      introAPROrRewards === IntroAPROrRewards.IntroAPR
        ? TWENTY_ONE_POINTS
        : THIRTY_SEVEN_POINTS,
    calculate: calculateDebtRewards,
  },
  ...(introAPROrRewards === IntroAPROrRewards.Rewards
    ? [
        {
          availablePoints: EIGHT_POINTS,
          calculate: calculateMonthlySpend,
        },
      ]
    : []),
];

const getDebtScore: CalculateMethodology = (answers, offer) =>
  calculateScore(answers, offer, getQuestions(answers));

export default getDebtScore;
